<template>
    <slide-over-panel v-model:show="showReasoning">
        <template #title>
            AI Alarm Analysis
        </template>
        <template #title-content>
            Root cause analysis for this alarm
        </template>
        <template #content>
            <div v-if="loading" class="flex justify-center py-8">
                <svg class="h-8 w-8 animate-spin text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>

            <div v-else-if="error" class="rounded-md border border-red-200 bg-red-50 p-4">
                <p class="text-sm text-red-800">{{ error }}</p>
            </div>

            <div v-else-if="reasoning" class="space-y-6">

                <div v-if="reasoning.summary" class="mx-4 mt-4 rounded-lg border border-blue-200 bg-blue-50 p-6">
                    <h3 class="m-0 mb-2 p-0 text-left text-xs font-semibold uppercase tracking-wide text-blue-900">Summary</h3>
                    <p class="m-0 p-0 text-lg font-medium leading-relaxed text-blue-900">{{ reasoning.summary }}</p>
                </div>

                <div class="border-b p-4">
                    <div class="flex items-center gap-2">
                        <span
                            class="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium"
                            :class="getScenarioClass(reasoning.scenario)"
                        >
                            {{ formatScenario(reasoning.scenario) }}
                        </span>

                        <span
                            v-if="reasoning.confidence"
                            class="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium"
                            :class="getConfidenceClass(reasoning.confidence)"
                        >
                            {{ reasoning.confidence }} Confidence
                        </span>
                    </div>
                </div>

                <div class="px-4">
                    <button
                        @click="showDetails = !showDetails"
                        class="mb-2 flex w-full items-center justify-between text-sm font-semibold text-gray-900 hover:text-blue-600"
                    >
                        <span>Details</span>
                        <svg
                            class="h-5 w-5 transform transition-transform"
                            :class="{ 'rotate-180': showDetails }"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    <div v-if="showDetails" class="mt-4 space-y-4">
                        <div v-if="reasoning.why">
                            <h4 class="mb-2 text-sm font-semibold text-gray-900">Root Cause Analysis</h4>
                            <p class="text-sm leading-relaxed text-gray-700">{{ reasoning.why }}</p>
                        </div>

                        <div v-if="reasoning.data_evidence">
                            <h4 class="mb-2 text-sm font-semibold text-gray-900">Data Evidence</h4>
                            <pre class="overflow-x-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">{{ reasoning.data_evidence }}</pre>
                        </div>

                        <div v-if="reasoning.confidence_explanation">
                            <h4 class="mb-2 text-sm font-semibold text-gray-900">Confidence Explanation</h4>

                            <div v-if="isNumberedList(reasoning.confidence_explanation)" class="text-sm leading-relaxed text-gray-700">
                                <p class="mb-2">{{ getListPreamble(reasoning.confidence_explanation) }}</p>
                                <ol class="ml-4 space-y-1 list-decimal">
                                    <li v-for="(item, idx) in parseNumberedList(reasoning.confidence_explanation)" :key="idx">
                                        {{ item }}
                                    </li>
                                </ol>
                            </div>

                            <div v-else-if="isBulletList(reasoning.confidence_explanation)" class="text-sm leading-relaxed text-gray-700">
                                <p v-if="getListPreamble(reasoning.confidence_explanation)" class="mb-2">
                                    {{ getListPreamble(reasoning.confidence_explanation) }}
                                </p>
                                <ul class="ml-4 space-y-1 list-disc">
                                    <li v-for="(item, idx) in parseBulletList(reasoning.confidence_explanation)" :key="idx">
                                        {{ item }}
                                    </li>
                                </ul>
                            </div>

                            <p v-else class="text-sm leading-relaxed text-gray-700">
                                {{ reasoning.confidence_explanation }}
                            </p>
                        </div>
                    </div>
                </div>

                <div v-if="!feedback.submitted" class="border-t border-gray-200 px-4 py-4">
                    <div class="relative rounded-md border-2 border-blue-100 bg-white p-4 transition-colors hover:border-blue-200">
                        <div class="absolute left-0 right-0 top-0 h-1 rounded-t-md bg-blue-700"></div>

                        <div class="mb-3 pt-1">
                            <div class="mb-1 flex items-center gap-2">
                                <span class="text-blue-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M12 11v.01" />
                                        <path d="M8 11v.01" />
                                        <path d="M16 11v.01" />
                                        <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3l12 0" />
                                    </svg>
                                </span>
                                <h4 class="m-0 p-0 text-sm font-semibold text-gray-900">Was this analysis helpful?</h4>
                            </div>
                        </div>

                        <div class="space-y-2">
                            <button
                                @click.stop="handleFeedbackRating('yes')"
                                class="w-full rounded-lg border-2 border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-green-700/20 hover:bg-green-50 hover:text-green-700"
                            >
                                <div class="flex items-center justify-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M7 11v8a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-7a1 1 0 0 1 1 -1h3a4 4 0 0 0 4 -4v-1a2 2 0 0 1 4 0v5h3a2 2 0 0 1 2 2l-1 5a2 3 0 0 1 -2 2h-7a3 3 0 0 1 -3 -3" />
                                    </svg>
                                    <span>Yes</span>
                                </div>
                            </button>

                            <button
                                @click.stop="handleFeedbackRating('unsure')"
                                class="w-full rounded-lg border-2 border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-yellow-700/20 hover:bg-yellow-50 hover:text-yellow-700"
                            >
                                <div class="flex items-center justify-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M8 8a3.5 3 0 0 1 3.5 -3h1a3.5 3 0 0 1 3.5 3a3 3 0 0 1 -2 3a3 4 0 0 0 -2 4" />
                                        <path d="M12 19l0 .01" />
                                    </svg>
                                    <span>Unsure</span>
                                </div>
                            </button>

                            <button
                                @click.stop="handleFeedbackRating('no')"
                                class="w-full rounded-lg border-2 border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-red-700/20 hover:bg-red-50 hover:text-red-700"
                            >
                                <div class="flex items-center justify-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M7 13v-8a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v7a1 1 0 0 0 1 1h3a4 4 0 0 1 4 4v1a2 2 0 0 0 4 0v-5h3a2 2 0 0 0 2 -2l-1 -5a2 3 0 0 0 -2 -2h-7a3 3 0 0 0 -3 3" />
                                    </svg>
                                    <span>No</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-else class="border-t border-gray-200 px-4 py-4">
                    <div class="relative rounded-md border-2 border-emerald-200 bg-white p-4">
                        <div class="absolute left-0 right-0 top-0 h-1 rounded-t-md bg-gradient-to-r from-emerald-500 to-teal-500"></div>

                        <div class="flex items-start gap-2 pt-1">
                            <svg class="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <div>
                                <h4 class="m-0 p-0 text-sm font-semibold text-gray-900">Thank you!</h4>
                                <p class="m-0 mt-1 p-0 text-sm text-gray-600">Your feedback has been received.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="border-t border-gray-200 px-4 pt-4">
                    <div class="space-y-1 text-xs text-gray-500">
                        <p><strong>Model:</strong> {{ reasoning.metadata?.model || 'Unknown' }}</p>
                        <p v-if="reasoning.metadata?.tokens_used"><strong>Tokens:</strong> {{ reasoning.metadata.tokens_used }}</p>
                    </div>
                </div>
            </div>
        </template>
    </slide-over-panel>

    <div v-if="showFeedbackModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="cancelFeedback">
        <div class="mx-4 w-full max-w-lg rounded-lg bg-white">
            <div class="p-6">
                <div class="mb-5">
                    <h3 class="m-0 mb-2 p-0 text-lg font-semibold text-gray-900">
                        {{ feedbackModalTitle }}
                    </h3>
                    <p class="m-0 p-0 text-base text-gray-600">
                        {{ feedbackModalSubtitle }}
                    </p>
                </div>

                <div class="mb-5">
                    <div class="flex flex-wrap gap-2">
                        <button
                            v-for="reason in feedbackReasons"
                            :key="reason"
                            @click="toggleReason(reason)"
                            :class="[
                                'rounded-lg border-2 px-3 py-2 text-sm transition-all',
                                feedback.reasons.includes(reason)
                                    ? 'border-blue-600 bg-blue-50 font-medium text-blue-700'
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-blue-700/20 hover:bg-blue-50'
                            ]"
                        >
                            {{ reason }}
                        </button>
                    </div>
                </div>

                <div class="mb-5">
                    <label class="mb-2 block text-sm font-medium text-gray-700">Additional comments (optional):</label>
                    <textarea
                        v-model="feedback.comment"
                        class="w-full resize-none rounded-lg border-2 border-gray-200 p-3 text-sm transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Any other details you'd like to share..."
                    ></textarea>
                </div>

                <div class="flex items-center justify-between gap-3">
                    <button
                        @click="cancelFeedback"
                        :disabled="feedback.submitting"
                        class="rounded-lg border-2 border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        @click="submitFeedback"
                        :disabled="feedback.submitting"
                        class="rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                    >
                        {{ feedback.submitting ? 'Submitting...' : 'Submit Feedback' }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <button
        @click="fetchReasoning"
        class="cursor-pointer rounded-md p-1.5 text-gray-700 transition-colors duration-75 hover:text-blue-700"
        title="View AI Analysis"
    >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M16 18a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2m0 -12a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2m-7 12a6 6 0 0 1 6 -6a6 6 0 0 1 -6 -6a6 6 0 0 1 -6 6a6 6 0 0 1 6 6" />
        </svg>
    </button>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useCompany } from '@/composables/useCompany';
import SlideOverPanel from "@/components/elements/SlideOverPanel.vue";

const props = defineProps({
    alarmId: {
        type: Number,
        required: true
    }
});

const showReasoning = ref(false);
const showDetails = ref(false);
const reasoning = ref(null);
const loading = ref(false);
const error = ref(null);

const feedback = ref({
    rating: null,
    reasons: [],
    comment: '',
    submitted: false,
    submitting: false
});
const showFeedbackModal = ref(false);

const {buildCompanyApiInternalRoute} = useCompany();

const feedbackReasons = computed(() => {
    if (feedback.value.rating === 'yes') {
        return [
            'Identified the root cause',
            'Helped prioritize the fix',
            'Saved troubleshooting time',
            'Confirmed my suspicions',
            'Provided actionable steps',
            'Prevented further investigation'
        ];
    } else if (feedback.value.rating === 'unsure') {
        return [
            'Not enough data evidence',
            'Uncertain about the conclusion',
            'Need more context',
            'Conflicting information',
            'Too technical',
            'Missing key details'
        ];
    } else if (feedback.value.rating === 'no') {
        return [
            'Wrong scenario identified',
            'Inaccurate data interpretation',
            'Missed the real issue',
            'Based on incomplete data',
            'Not helpful for this case',
            'Contradicts what I know'
        ];
    }
    return [];
});

const feedbackModalTitle = computed(() => {
    if (feedback.value.rating === 'yes') {
        return 'What did this analysis help you do?';
    } else if (feedback.value.rating === 'unsure') {
        return 'What made you unsure?';
    } else if (feedback.value.rating === 'no') {
        return 'What went wrong?';
    }
    return '';
});

const feedbackModalSubtitle = computed(() => {
    if (feedback.value.rating === 'yes') {
        return 'Select any that apply';
    } else if (feedback.value.rating === 'unsure') {
        return 'Help us improve the analysis';
    } else if (feedback.value.rating === 'no') {
        return 'Help us understand the issue';
    }
    return '';
});

const fetchReasoning = async () => {
    showReasoning.value = true;
    loading.value = true;
    error.value = null;
    reasoning.value = null;

    try {
        const response = await axios.get(
            buildCompanyApiInternalRoute(`alarms/${props.alarmId}/reasoning`)
        );
        reasoning.value = response.data;
    } catch (err) {
        error.value = err.response?.data?.message || 'Failed to load AI reasoning. Please try again.';
        console.error('Error fetching AI reasoning:', err);
    } finally {
        loading.value = false;
    }
};

const handleFeedbackRating = (rating) => {
    feedback.value.rating = rating;
    feedback.value.reasons = [];
    showFeedbackModal.value = true;
};

const toggleReason = (reason) => {
    const index = feedback.value.reasons.indexOf(reason);
    if (index > -1) {
        feedback.value.reasons.splice(index, 1);
    } else {
        feedback.value.reasons.push(reason);
    }
};

const submitFeedback = async () => {
    try {
        feedback.value.submitting = true;

        const feedbackData = {
            rating: feedback.value.rating,
            reasons: feedback.value.reasons,
            comment: feedback.value.comment,
            analysis_output: {
                scenario: reasoning.value.scenario,
                summary: reasoning.value.summary,
                why: reasoning.value.why,
                data_evidence: reasoning.value.data_evidence,
                confidence: reasoning.value.confidence,
                confidence_explanation: reasoning.value.confidence_explanation,
            },
            alarm_id: props.alarmId,
            timestamp: new Date().toISOString()
        };

        await axios.post(
            buildCompanyApiInternalRoute(`alarms/${props.alarmId}/reasoning/feedback`),
            feedbackData
        );

        feedback.value.submitted = true;
        showFeedbackModal.value = false;

    } catch (error) {
        console.error('Failed to submit feedback:', error);
        alert('Failed to submit feedback. Please try again.');
    } finally {
        feedback.value.submitting = false;
    }
};

const cancelFeedback = () => {
    showFeedbackModal.value = false;
    feedback.value.rating = null;
    feedback.value.reasons = [];
    feedback.value.comment = '';
};

const formatScenario = (scenario) => {
    const scenarios = {
        'DEVICE_ISSUE': 'Device Issue',
        'THRESHOLD_ISSUE': 'Threshold Issue',
        'ENVIRONMENTAL_ISSUE': 'Environmental Issue',
        'RESOLVED_ISSUE': 'Resolved Issue',
        'UNKNOWN': 'Unknown'
    };
    return scenarios[scenario] || scenario;
};

const getScenarioClass = (scenario) => {
    const classes = {
        'DEVICE_ISSUE': 'bg-orange-100 text-orange-800',
        'THRESHOLD_ISSUE': 'bg-blue-100 text-blue-800',
        'ENVIRONMENTAL_ISSUE': 'bg-red-100 text-red-800',
        'RESOLVED_ISSUE': 'bg-green-100 text-green-800',
        'UNKNOWN': 'bg-gray-100 text-gray-800'
    };
    return classes[scenario] || classes.UNKNOWN;
};

const getConfidenceClass = (confidence) => {
    const classes = {
        'HIGH': 'bg-green-100 text-green-800',
        'MEDIUM': 'bg-yellow-100 text-yellow-800',
        'LOW': 'bg-orange-100 text-orange-800',
        'UNKNOWN': 'bg-gray-100 text-gray-800'
    };
    return classes[confidence] || classes.UNKNOWN;
};

const isNumberedList = (text) => {
    return /\n\d+\.\s/.test(text);
};

const isBulletList = (text) => {
    return /\n-\s/.test(text);
};

const getListPreamble = (text) => {
    // Match text before numbered list or bullet list
    const match = text.match(/^(.+?)(?=\n(?:\d+\.|-)\s)/s);
    return match ? match[1].trim() : '';
};

const parseNumberedList = (text) => {
    const matches = text.match(/\d+\.\s(.+?)(?=\n\d+\.\s|\n*$)/gs);
    return matches ? matches.map(m => m.replace(/^\d+\.\s/, '').trim()) : [];
};

const parseBulletList = (text) => {
    const matches = text.match(/-\s(.+?)(?=\n-\s|\n*$)/gs);
    return matches ? matches.map(m => m.replace(/^-\s/, '').trim()) : [];
};
</script>